/**
 * Placeholder data-quality data. Replace each export with the corresponding
 * API response once the quality-engine endpoints are available.
 */

export const LAST_UPDATED = "01 Sep 2026, 10:45 AM";

export const SUMMARY_CARDS = [
  {
    key: "score",
    label: "Overall Data Quality Score",
    value: "96.8%",
    delta: "2.4% vs last period",
    tone: "green",
    icon: "shield",
    progress: 96.8,
  },
  {
    key: "records",
    label: "Total Taxpayer Records",
    value: "3,200,000",
    foot: "100% of integrated data",
    tone: "blue",
    icon: "database",
  },
  {
    key: "valid",
    label: "Valid Records",
    value: "3,097,456",
    foot: "96.8% of total records",
    tone: "green",
    icon: "check",
  },
  {
    key: "issues",
    label: "Records with Issues",
    value: "102,544",
    foot: "3.2% of total records",
    tone: "amber",
    icon: "alert",
  },
  {
    key: "rules",
    label: "Rules Executed",
    value: "48",
    foot: "Active quality rules",
    tone: "indigo",
    icon: "rules",
  },
];

/**
 * Issue-type colours. Validated as a categorical set for colour-vision
 * separation and contrast; each series also carries its own marker shape in
 * the trend chart, so identity never rests on colour alone.
 */
export const ISSUE_COLORS = {
  duplicates: "#d55e00",
  missing: "#e69f00",
  conflicts: "#cc79a7",
  outdated: "#009e73",
  invalid: "#0072b2",
  others: "#9aa7bb",
};

export const TOTAL_ISSUES = 102544;

export const ISSUE_BREAKDOWN = [
  {
    label: "Duplicates",
    value: 18421,
    share: 17.95,
    color: ISSUE_COLORS.duplicates,
    legendPrimary: "18,421",
    legendSecondary: "(17.95%)",
  },
  {
    label: "Missing Data",
    value: 34820,
    share: 33.93,
    color: ISSUE_COLORS.missing,
    legendPrimary: "34,820",
    legendSecondary: "(33.93%)",
  },
  {
    label: "Conflicts",
    value: 12876,
    share: 12.54,
    color: ISSUE_COLORS.conflicts,
    legendPrimary: "12,876",
    legendSecondary: "(12.54%)",
  },
  {
    label: "Outdated Records",
    value: 26430,
    share: 25.76,
    color: ISSUE_COLORS.outdated,
    legendPrimary: "26,430",
    legendSecondary: "(25.76%)",
  },
  {
    label: "Invalid Format",
    value: 6997,
    share: 6.82,
    color: ISSUE_COLORS.invalid,
    legendPrimary: "6,997",
    legendSecondary: "(6.82%)",
  },
  {
    label: "Others",
    value: 1000,
    share: 9.98,
    color: ISSUE_COLORS.others,
    legendPrimary: "1,000",
    legendSecondary: "(9.98%)",
  },
];

export const TREND_MONTHS = [
  "Jan 2026",
  "Feb 2026",
  "Mar 2026",
  "Apr 2026",
  "May 2026",
  "Jun 2026",
  "Jul 2026",
  "Aug 2026",
  "Sep 2026",
];

export const TREND_SERIES = [
  {
    key: "duplicates",
    label: "Duplicates",
    color: ISSUE_COLORS.duplicates,
    values: [30500, 29800, 28400, 27100, 25200, 23800, 21400, 17842, 18421],
  },
  {
    key: "missing",
    label: "Missing Data",
    color: ISSUE_COLORS.missing,
    values: [95200, 88400, 74600, 81200, 69800, 58400, 47600, 36120, 34820],
  },
  {
    key: "conflicts",
    label: "Conflicts",
    color: ISSUE_COLORS.conflicts,
    values: [20100, 19400, 18600, 17200, 16400, 15100, 14200, 12845, 12876],
  },
  {
    key: "outdated",
    label: "Outdated Records",
    color: ISSUE_COLORS.outdated,
    values: [34200, 33100, 31800, 30600, 29400, 28100, 27000, 25680, 26430],
  },
  {
    key: "invalid",
    label: "Invalid Format",
    color: ISSUE_COLORS.invalid,
    values: [9500, 9200, 8900, 8600, 8200, 7800, 7300, 6642, 6997],
  },
];

export const TREND_MAX = 100000;

export const RULE_VIOLATIONS = [
  { rule: "Missing Address", records: "18,742", severity: "High" },
  { rule: "Duplicate NIF", records: "12,635", severity: "High" },
  { rule: "Invalid NIF Format", records: "6,997", severity: "Medium" },
  { rule: "Missing Phone Number", records: "6,285", severity: "Medium" },
  { rule: "Tax Regime Conflict", records: "4,876", severity: "Medium" },
];

export const ISSUE_FILTERS = [
  {
    key: "type",
    placeholder: "All Issue Types",
    options: [
      "Duplicate Record",
      "Missing Data",
      "Data Conflict",
      "Outdated Record",
      "Invalid Format",
    ],
  },
  {
    key: "severity",
    placeholder: "All Severity Levels",
    options: ["High", "Medium", "Low"],
  },
  {
    key: "source",
    placeholder: "All Data Sources",
    options: ["DGI Register", "Business Registry", "VAT System"],
  },
  {
    key: "status",
    placeholder: "All Status",
    options: ["New", "In Review", "Resolved"],
  },
];

export const TOTAL_ISSUE_ROWS = 102544;

export const RECENT_ISSUES = [
  {
    id: 1,
    name: "ABC Trading Ltd.",
    nif: "100-456-789",
    type: "Duplicate Record",
    description: "Potential duplicate found with 92% match",
    source: "DGI Register",
    detected: "01 Sep 2026, 10:32 AM",
    severity: "High",
    status: "New",
  },
  {
    id: 2,
    name: "Prime Logistics SARL",
    nif: "300-345-678",
    type: "Missing Data",
    description: "Missing registration address",
    source: "Business Registry",
    detected: "01 Sep 2026, 09:48 AM",
    severity: "High",
    status: "In Review",
  },
  {
    id: 3,
    name: "Delta Services Ltd.",
    nif: "400-567-890",
    type: "Data Conflict",
    description: "Tax regime mismatch with VAT system",
    source: "VAT System",
    detected: "01 Sep 2026, 09:15 AM",
    severity: "Medium",
    status: "New",
  },
  {
    id: 4,
    name: "Greenfield Imports",
    nif: "500-678-901",
    type: "Outdated Record",
    description: "Last activity more than 24 months ago",
    source: "DGI Register",
    detected: "01 Sep 2026, 08:40 AM",
    severity: "Medium",
    status: "In Review",
  },
  {
    id: 5,
    name: "Sunrise Construction",
    nif: "600-789-012",
    type: "Invalid Format",
    description: "Invalid phone number format",
    source: "Business Registry",
    detected: "01 Sep 2026, 08:05 AM",
    severity: "Low",
    status: "New",
  },
];
